import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { Minus, Plus } from 'lucide-react';

/**
 * V6 — Modal for backing a crowdfunding campaign with slices.
 *
 * Props:
 *   isOpen              — boolean
 *   onClose             — () => void
 *   campaign            — { id, title }
 *   balance             — current slice balance
 *   isActiveSubscriber  — whether user has a subscription
 *   onSuccess           — () => void — called after successful backing
 */
const BackCampaignModal = ({ isOpen, onClose, campaign, balance, isActiveSubscriber, onSuccess }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [amount, setAmount] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const safeBalance = balance ?? 0;

  useEffect(() => {
    if (isOpen) setAmount(1);
  }, [isOpen]);

  const clamped = Math.max(1, Math.min(amount, safeBalance));

  const setAmountSafe = (val) => {
    const n = parseInt(val, 10);
    if (!isNaN(n)) setAmount(Math.max(1, Math.min(n, safeBalance)));
  };

  const handleBack = async () => {
    if (clamped < 1 || clamped > safeBalance) return;
    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('user_back_campaign', {
        p_campaign_id: campaign.id,
        p_slices_amount: clamped,
      });
      if (error) throw error;

      if (data?.success) {
        window.dispatchEvent(new Event('sliceBalanceChanged'));
        toast({
          title: `Backed with ${clamped} slice${clamped !== 1 ? 's' : ''}!`,
          description: `You're supporting "${campaign.title}". Thank you!`,
        });
        onSuccess?.();
        onClose();
      } else {
        toast({
          title: 'Could not back campaign',
          description: data?.error || 'Something went wrong.',
          variant: 'destructive',
        });
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to back campaign.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const renderNoSubscription = () => (
    <>
      <DialogHeader>
        <DialogTitle>Subscribe to Back Campaigns</DialogTitle>
        <DialogDescription>
          Slice allocations are included with all subscription plans. Subscribe to support creators through crowdfunding.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="mt-4">
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={() => { onClose(); navigate('/pricing'); }}>View Plans</Button>
      </DialogFooter>
    </>
  );

  const renderNoBalance = () => (
    <>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <span>🍕</span> No Slices Available
        </DialogTitle>
        <DialogDescription>
          You've used all your slices for this month. Your balance resets on your next billing date.
        </DialogDescription>
      </DialogHeader>
      <DialogFooter className="mt-4">
        <Button variant="ghost" onClick={onClose}>Close</Button>
        <Button variant="outline" onClick={() => { onClose(); navigate('/slices'); }}>
          View History
        </Button>
      </DialogFooter>
    </>
  );

  const QUICK_AMOUNTS = [1, 3, 5];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-sm">
        {!isActiveSubscriber ? (
          renderNoSubscription()
        ) : safeBalance === 0 ? (
          renderNoBalance()
        ) : (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <span>🍕</span> Back This Campaign
              </DialogTitle>
              <DialogDescription>
                Supporting{' '}
                <span className="font-medium text-foreground">{campaign?.title}</span>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {/* Balance */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Your balance</span>
                <span className="font-semibold">🍕 {safeBalance} slices</span>
              </div>

              {/* Amount selector */}
              <div className="space-y-2">
                <label className="text-sm font-medium">Slices to pledge</label>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => setAmount(a => Math.max(1, a - 1))}
                    disabled={clamped <= 1}
                  >
                    <Minus className="h-4 w-4" />
                  </Button>
                  <input
                    type="number"
                    min={1}
                    max={safeBalance}
                    value={clamped}
                    onChange={e => setAmountSafe(e.target.value)}
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-center text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    className="h-9 w-9 shrink-0"
                    onClick={() => setAmount(a => Math.min(a + 1, safeBalance))}
                    disabled={clamped >= safeBalance}
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </div>

                {/* Quick select */}
                <div className="flex gap-2 flex-wrap">
                  {QUICK_AMOUNTS.map(q => (
                    <Button
                      key={q}
                      type="button"
                      variant={clamped === q ? 'default' : 'outline'}
                      size="sm"
                      className="text-xs h-7 px-3"
                      onClick={() => setAmountSafe(q)}
                      disabled={q > safeBalance}
                    >
                      {q}
                    </Button>
                  ))}
                  <Button
                    type="button"
                    variant={clamped === safeBalance ? 'default' : 'outline'}
                    size="sm"
                    className="text-xs h-7 px-3"
                    onClick={() => setAmountSafe(safeBalance)}
                  >
                    All {safeBalance}
                  </Button>
                </div>
              </div>

              {/* Preview */}
              <div className="rounded-lg bg-muted/50 border border-border/50 p-3 text-sm space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Pledging</span>
                  <span className="font-semibold text-primary">🍕 {clamped} slices</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Balance after</span>
                  <span className="font-medium">🍕 {safeBalance - clamped} slices</span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="ghost" onClick={onClose} disabled={isLoading}>
                Cancel
              </Button>
              <Button onClick={handleBack} disabled={isLoading}>
                {isLoading ? 'Pledging…' : `Pledge ${clamped} Slice${clamped !== 1 ? 's' : ''}`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BackCampaignModal;
